import { Input, Mail, PhoneInput, Select, User } from "@ews-admin/global-design-system";
import React from "react";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CivilityEnum, CivilityType } from "../../../../types/user.definition";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StaffFormData = any;

interface PersonalInfoSectionProps {
  control: Control<StaffFormData>;
  setValue: UseFormSetValue<StaffFormData>;
  errors?: FieldErrors<StaffFormData>;
  emailDisabled?: boolean;
  phoneDisabled?: boolean;
  dateOfBirthRequired?: boolean;
}

export const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({
  control,
  setValue,
  emailDisabled = false,
  phoneDisabled = false,
  dateOfBirthRequired = true,
}) => {
  const { t } = useTranslation();
  return (
    <div>
      <h4 className="mb-4 text-lg font-medium text-neutral-900">
        {t("common.fieldLabels.personalInformation")}
      </h4>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Controller
          name="civility"
          control={control}
          render={({ field, fieldState }) => (
            <Select
              required
              label={t("common.fieldLabels.civility")}
              options={[
                { value: CivilityEnum.MR, label: t("common.fieldLabels.mr") },
                { value: CivilityEnum.MRS, label: t("common.fieldLabels.mrs") },
              ]}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                setValue("civility" as never, value as CivilityType as never, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="firstName"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              required
              label={t("common.fieldLabels.firstName")}
              placeholder={t("common.fieldPlaceholders.firstName")}
              type="text"
              {...field}
              leftIcon={<User size={16} />}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="lastName"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              required
              label={t("common.fieldLabels.lastName")}
              placeholder={t("common.fieldPlaceholders.lastName")}
              type="text"
              {...field}
              leftIcon={<User size={16} />}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="email"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              required
              label={t("common.fieldLabels.email")}
              placeholder={t("common.fieldPlaceholders.email")}
              type="email"
              {...field}
              disabled={emailDisabled}
              leftIcon={<Mail size={16} />}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="phoneNumber"
          control={control}
          render={({ field, fieldState }) => (
            <PhoneInput
              required
              label={t("common.fieldLabels.phoneNumber")}
              {...field}
              disabled={phoneDisabled}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="dateOfBirth"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              required={dateOfBirthRequired}
              label={t("common.fieldLabels.dateOfBirth")}
              type="date"
              {...field}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>
    </div>
  );
};
